/**
 * Standing editorial copy for the Financial section: the section intro, the
 * sourcing methodology, and the disclosures.
 *
 * The methodology and disclosures are ported from Realiform. Two edits were
 * necessary rather than cosmetic: the brand name changed, and every reference
 * to holding a paid membership was removed, because there is no membership
 * here and a disclosure that describes a product the site does not sell is
 * worse than no disclosure.
 */

/* -------------------------------------------------------------------------- */
/*                               Section intro                                 */
/* -------------------------------------------------------------------------- */

/**
 * Why wealth-building sits on a Korean diaspora site.
 *
 * This is load-bearing, not throat-clearing. Most of this section is US
 * micro-business analysis with no Korean angle, arriving next to F-4 visa and
 * remittance guides. A reader who cannot tell why deserves an answer in the
 * first screen, and so does a policy reviewer.
 */
export const SECTION_INTRO = {
  eyebrow: 'Financial',
  title: 'Money you make, not just money you move',
  titleKo: '금융',
  lede: 'The rest of this site covers money crossing a border — transfer fees, exchange-rate margins, what the IRS and the NTS expect you to report. This section covers where the money came from first: small businesses with published numbers, the calculators to underwrite them, and honest accounting of what each one actually pays per hour.',
  body: [
    'Remittance is the end of a story. Someone earned it, and how they earned it is the part nobody writes down with real figures attached. The teardowns here are mostly American small businesses — laundromats, vending routes, a niche software tool — because that is where the documented economics are, and the mechanics of a seller note or an SDE multiple do not change nationality.',
    'Where the diaspora angle is real, it is treated as its own subject rather than a paragraph bolted onto someone else’s analysis. Buying a business from the generation that built it. Running US-source income from a desk in Seoul on an F-4. Teaching a language that a family is losing. Those pieces cite primary sources and are marked accordingly.',
    'Every figure on every page is labelled with where it came from. Most are modelled from published ranges rather than reported by an operator, and the badge on each study says so. That labelling is the whole product — a finance section that blurs a modelled number into a reported one has nothing left worth reading.',
  ],
} as const;

/* -------------------------------------------------------------------------- */
/*                                 Methodology                                 */
/* -------------------------------------------------------------------------- */

export type SourcingTier = {
  badge: string;
  meaning: string;
  limits: string;
};

export const SOURCING_TIERS: SourcingTier[] = [
  {
    badge: 'Operator verified',
    meaning:
      'Figures came directly from the person running the business, and we saw supporting documents — bank statements, platform dashboards, or filed returns — for the headline numbers.',
    limits:
      'Still a single operator in a single market. It does not mean the same business would produce the same result for you.',
  },
  {
    badge: 'Public filing',
    meaning:
      'Numbers taken from documents the business is legally required to publish or disclose.',
    limits:
      'Filings lag reality and use accounting conventions that can obscure cash position.',
  },
  {
    badge: 'Listing data',
    meaning:
      'Economics drawn from active or completed business-for-sale listings, which state asking price, revenue, and claimed earnings.',
    limits:
      'Listing figures are seller-provided marketing material. We treat them as claims, not facts, and say so.',
  },
  {
    badge: 'Composite',
    meaning:
      'Assembled from several real businesses of the same type, blended into one representative case so no individual operator is identifiable.',
    limits:
      'No single business had exactly these numbers. The shape is real; the specific figures are a synthesis.',
  },
  {
    badge: 'Illustrative',
    meaning:
      'Modelled from published industry ranges to demonstrate a mechanism, where we did not have access to a specific business.',
    limits:
      'These are worked examples, not reports. They show how the economics behave, not what any particular operator earned.',
  },
];

export const METHODOLOGY_REFUSALS: string[] = [
  'Publish a revenue figure without stating how it was obtained.',
  'Run a teardown without its failure modes. Every case study carries a risk section, and the section is written before the piece is approved.',
  'Present survivor cases as representative. Most attempts at most of these businesses do not clear the returns shown, and the studies say so where we know it.',
  'Let a paid placement change what appears in an analysis. Affiliate relationships, where they exist, are disclosed on the page.',
];

/* -------------------------------------------------------------------------- */
/*                                 Disclosures                                 */
/* -------------------------------------------------------------------------- */

export type DisclosureSection = {
  heading: string;
  body: string;
};

export const DISCLOSURES: DisclosureSection[] = [
  {
    heading: 'Educational content only',
    body: 'This section publishes financial education, business analysis, and estimation tools. Nothing here is investment, tax, legal, or accounting advice, and nothing constitutes a recommendation, offer, or solicitation to buy, sell, or operate any business, security, or financial instrument. No advisory or fiduciary relationship is created by reading these pages or using their calculators.',
  },
  {
    heading: 'Results are not typical or predictable',
    body: 'Case studies describe outcomes for specific or modelled businesses under specific conditions. Outcomes in small business vary enormously with market, timing, capital, competition, and operator skill. Most attempts at most business models do not reach the returns shown in any published case study, here or anywhere else. Past performance of any business does not indicate future results.',
  },
  {
    heading: 'Calculators produce estimates',
    body: 'Every calculator here returns an estimate derived only from the figures you enter. They omit factors that materially affect real outcomes, including working-capital swings, capital expenditure timing, seasonality, financing covenants, and local regulation. They are decision-support tools for learning, not underwriting systems, and no output should be relied on to commit capital.',
  },
  {
    heading: 'Tax content and figures',
    body: 'The solo tax estimator produces a rough United States federal estimate using published parameters for a single tax year. It excludes state and local tax entirely, assumes the standard deduction, and applies no credits. It does not model Korean tax at all: a reader who is tax-resident in Korea cannot use it to estimate a Korean liability. Tax parameters change annually and legislation may change them mid-year. The estimator must not be used to prepare or file a return, and nothing in it establishes a filing position. Consult a CPA or enrolled agent, and where Korean tax is involved, a licensed Korean tax accountant (세무사).',
  },
  {
    heading: 'Cross-border content',
    body: 'Case studies and guidance touching immigration status, tax residency, or social-security coverage across the United States and Korea summarise published rules as of the date shown on the page. These rules change, are applied with official discretion, and turn on facts specific to each person. Nothing here establishes your residency, your visa eligibility, or your filing position in either country.',
  },
  {
    heading: 'Affiliate relationships',
    body: 'KoreanHub may earn a commission from some tools or services mentioned on this site. Any such relationship is disclosed on the page where it appears. Affiliate arrangements never determine whether a tool appears in a case study — the tooling listed in a teardown reflects what the operator actually uses.',
  },
  {
    heading: 'No personal information in case studies',
    body: 'Case studies marked composite or illustrative are constructed so that no individual operator or business is identifiable. Where an operator has shared figures directly, they have agreed to publication and to the level of detail shown.',
  },
  {
    heading: 'Accuracy and corrections',
    body: 'We aim to be accurate and to label uncertainty honestly. Where a figure is found to be wrong, it is corrected in place with a dated note rather than quietly edited. If you believe something here is incorrect, tell us and we will look at it.',
  },
];
